import { useState } from "react";
import { X, ArrowRight, RotateCcw, AlertTriangle, CheckCircle, Pencil, Check } from "lucide-react";

/* Canvas surface, same shell as Settings — cards on --bg-surface over
   --bg-primary, so it follows the Light / Dark panel toggle. */
const card = { background: "var(--bg-surface)", borderRadius: 12, padding: 20, boxShadow: "var(--shadow-soft)" };
const heading = {
  margin: 0, fontSize: 13, fontWeight: 700, color: "var(--text-primary)",
  textTransform: "uppercase", letterSpacing: "0.5px",
};
const field = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1.5px solid var(--border-strong)", fontSize: 14,
  outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  background: "var(--input-bg)", color: "var(--text-primary)",
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
};

/**
 * Her niche, framed as a history she can move around in rather than a setting.
 *
 * That framing is the point. A pivot feels catastrophic because it sounds
 * irreversible — so the screen leads with the list of niches she's held and a
 * roll-back on each one, and treats "change my niche" as the secondary action.
 * Nothing here deletes anything: changing niche makes the old one inactive, and
 * inactive niches stay listed forever.
 */
export function NichePanel({
  nicheVersions = [],
  activeNiche,
  staleCounts,
  isMobile,
  onBack,
  onCreateVersion,
  onSwitchTo,
  onRename,
  onOpenMission,
  onViewReview,
}) {
  const [changing, setChanging] = useState(false);
  const [label, setLabel] = useState("");
  const [statement, setStatement] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [rename, setRename] = useState("");

  const staleTotal = (staleCounts?.redo || 0) + (staleCounts?.mixed || 0) + (staleCounts?.check || 0);
  // Active first, then the rest newest-first as they arrive from Supabase.
  const ordered = [
    ...nicheVersions.filter((v) => v.id === activeNiche?.id),
    ...nicheVersions.filter((v) => v.id !== activeNiche?.id),
  ];

  const submit = async () => {
    if (!label.trim() || submitting) return;
    setSubmitting(true);
    await onCreateVersion(label.trim(), statement.trim());
    setSubmitting(false);
    setChanging(false);
    setLabel("");
    setStatement("");
  };

  const commitRename = (id) => {
    if (rename.trim()) onRename?.(id, rename);
    setRenamingId(null);
  };

  const versionRow = (v) => {
    const isActive = v.id === activeNiche?.id;
    const date = fmtDate(v.activated_at || v.created_at);
    return (
      <div
        key={v.id}
        style={{
          padding: "14px 15px", borderRadius: 10,
          border: isActive ? "1px solid var(--accent-line)" : "1px solid var(--border-subtle)",
          background: isActive ? "var(--accent-quiet)" : "var(--bg-primary)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {renamingId === v.id ? (
              <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                <input
                  value={rename}
                  autoFocus
                  onChange={(e) => setRename(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(v.id);
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  style={{ ...field, fontSize: 14, fontWeight: 600, padding: "6px 10px" }}
                />
                <button
                  onClick={() => commitRename(v.id)}
                  title="Save name"
                  style={{
                    flexShrink: 0, padding: 7, borderRadius: 7, border: "none",
                    background: "var(--btn-bg)", cursor: "pointer", display: "flex",
                  }}
                >
                  <Check size={12} color="var(--btn-text)" />
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{v.label}</span>
                {isActive && (
                  <span style={{
                    flexShrink: 0, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em",
                    textTransform: "uppercase", color: "var(--accent)",
                    border: "1px solid var(--accent-line)", borderRadius: 5, padding: "1px 5px",
                  }}>
                    Active
                  </span>
                )}
                {onRename && (
                  <button
                    onClick={() => { setRename(v.label); setRenamingId(v.id); }}
                    title="Rename — this is just a label for finding it here, not your niche itself"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}
                  >
                    <Pencil size={11} color="var(--text-muted)" />
                  </button>
                )}
              </div>
            )}

            {/* The statement is deliberately read-only. It's a locked output from
                My Mission — that's where Brill writes it with her. Editing it here
                too would give the same sentence two homes and let them drift, and
                the one in My Mission is what every other module builds from. */}
            {v.statement && (
              <p style={{
                margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.65, color: "var(--text-secondary)",
                borderLeft: "2px solid var(--border-subtle)", paddingLeft: 10,
              }}>
                {v.statement}
              </p>
            )}
            {isActive && onOpenMission && (
              <button
                onClick={onOpenMission}
                style={{
                  marginTop: 8, padding: 0, background: "none", border: "none",
                  cursor: "pointer", fontFamily: "inherit", display: "flex",
                  alignItems: "center", gap: 4,
                  fontSize: 11.5, color: "var(--accent)", fontWeight: 600,
                }}
              >
                <Pencil size={10} />
                {v.statement ? "Change the wording in My Mission" : "Write your niche statement in My Mission"}
              </button>
            )}
            {date && (
              <div style={{ fontSize: 11, color: "var(--text-disabled)", marginTop: 6 }}>
                {isActive ? `Active since ${date}` : `Was active from ${date}`}
              </div>
            )}
          </div>

          {!isActive && (
            <button
              onClick={() => onSwitchTo(v.id)}
              style={{
                flexShrink: 0, padding: "7px 12px", borderRadius: 8,
                border: "1px solid var(--border-strong)", background: "var(--bg-surface)",
                color: "var(--text-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
              }}
            >
              <RotateCcw size={11} />
              Roll back
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      flex: 1, overflowY: "auto", background: "var(--bg-primary)",
      padding: isMobile ? "20px 16px 100px" : "32px 32px 40px",
      display: "flex", flexDirection: "column", gap: 20,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>Your Niche</h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            Every niche you've built on. Nothing here is ever deleted — you can roll back at any time.
          </p>
        </div>
        <button onClick={() => onBack()} style={{
          background: "var(--btn-bg)", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
          fontSize: 13, color: "var(--btn-text)", fontWeight: 600, flexShrink: 0,
        }}>
          <X size={13} />
          Close
        </button>
      </div>

      {/* ── Anything still built on an older niche ────────────────────────── */}
      {!changing && staleTotal > 0 && (
        <button
          onClick={onViewReview}
          style={{
            ...card, border: "1px solid var(--accent-line)", cursor: "pointer",
            textAlign: "left", fontFamily: "inherit", display: "block", width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ ...heading }}>
                {staleTotal} {staleTotal === 1 ? "module" : "modules"} built on an earlier niche
              </div>
              {/* Counted in modules, not sections. A section count is arithmetic —
                  "11 need real work" spread over five modules has no shape to it,
                  and reads as start over. Modules are the unit she actually works
                  in, and the number is small enough to look at. */}
              <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.6, color: "var(--text-secondary)" }}>
                {[
                  staleCounts.redo > 0 && `${staleCounts.redo} to rebuild`,
                  staleCounts.mixed > 0 && `${staleCounts.mixed} mostly a read-through`,
                  staleCounts.check > 0 && `${staleCounts.check} to look over`,
                ].filter(Boolean).join(", ")}{staleCounts.redo > 0 ? " — questions, mostly." : "."}
              </p>
            </div>
            <ArrowRight size={15} color="var(--accent)" style={{ flexShrink: 0 }} />
          </div>
        </button>
      )}

      {/* ── The versions ──────────────────────────────────────────────────── */}
      {ordered.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {ordered.map(versionRow)}
        </div>
      ) : (
        <div style={card}>
          <div style={heading}>No niche yet</div>
          <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.7, color: "var(--text-secondary)" }}>
            Your niche gets built in <strong style={{ color: "var(--text-primary)" }}>My Mission</strong> — the one
            problem, who you're for, the transformation, and your niche statement. Once those are locked they'll appear
            here and you'll be able to change them without losing anything.
          </p>
        </div>
      )}

      {/* ── Changing niche — secondary to the history above ───────────────── */}
      {!changing && activeNiche && (
        <button
          onClick={() => setChanging(true)}
          style={{
            padding: "11px 14px", borderRadius: 10,
            border: "1px solid var(--border-strong)", background: "var(--bg-surface)",
            color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            fontFamily: "inherit",
          }}
        >
          <RotateCcw size={13} />
          My niche has changed
        </button>
      )}

      {changing && (
        <div style={{ ...card, border: "1px solid var(--accent-line)" }}>
          <div style={heading}>What's the new niche?</div>
          <p style={{ margin: "6px 0 16px", fontSize: 13, lineHeight: 1.7, color: "var(--text-secondary)" }}>
            Rough is fine — Brill will sharpen it with you. Your current niche stays in the list above, so you can roll
            back whenever you want.
          </p>

          <label style={{ display: "block", marginBottom: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-muted)" }}>
            Give it a short name <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(just so you can find it here)</span>
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Postpartum fitness coaches"
            style={{ ...field, marginBottom: 16 }}
          />

          <label style={{ display: "block", marginBottom: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-muted)" }}>
            Who you serve now, in a line <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional — a starting point, you'll build the real one in My Mission)</span>
          </label>
          <textarea
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="I help… who are struggling with… get to…"
            style={{ ...field, minHeight: 84, resize: "vertical", lineHeight: 1.6 }}
          />

          <div style={{
            marginTop: 16, padding: "12px 14px", borderRadius: 10,
            background: "var(--accent-quiet)", border: "1px solid var(--accent-line)",
          }}>
            <div style={{ display: "flex", gap: 8 }}>
              <AlertTriangle size={13} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.65, color: "var(--text-secondary)" }}>
                Nothing you've locked gets rewritten without you choosing it. Brill will show you exactly what this
                touches, and what it doesn't.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => { setChanging(false); setLabel(""); setStatement(""); }}
              style={{
                flex: 1, padding: "11px 14px", borderRadius: 10,
                border: "1px solid var(--border-strong)", background: "var(--bg-primary)",
                color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Never mind
            </button>
            <button
              onClick={submit}
              disabled={!label.trim() || submitting}
              style={{
                flex: 2, padding: "11px 14px", borderRadius: 10, border: "none",
                background: label.trim() ? "var(--btn-bg)" : "var(--btn-off-bg)",
                color: label.trim() ? "var(--btn-text)" : "var(--btn-off-text)",
                fontSize: 13, fontWeight: 600, cursor: label.trim() ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                fontFamily: "inherit",
              }}
            >
              {submitting ? "Saving…" : <>Make this my niche <ArrowRight size={13} /></>}
            </button>
          </div>
        </div>
      )}

      {!changing && activeNiche && staleTotal === 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "0 2px" }}>
          <CheckCircle size={13} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.7, color: "var(--text-secondary)" }}>
            Everything you've locked is built on your current niche.
          </p>
        </div>
      )}
    </div>
  );
}
